import React from 'react'
import cx from 'classnames'
import * as Icon from '@manychat/icons'

import { AdoptionComponent } from '../../constants/AdoptionComponent'
import tints from '../../constants/tints.json'
import { useDsAdoptionAttribute } from '../../utils/dsAdoption'

import { SectionMessageProps, SectionMessageVariant } from './interfaces'

import cm from './SectionMessage.module.css'

const DEFAULT_ICON_SIZE = 20

const iconsConfig: Record<SectionMessageVariant, { icon: keyof typeof Icon; color: string }> = {
  info: { icon: 'Info', color: tints.blue[2] },
  warning: { icon: 'Warning', color: tints.yellow[2] },
  error: { icon: 'Attention', color: tints.red[2] },
}

/**
 * SectionMessage is a component that displays a message with an icon and a title.
 *
 * [UI Guidelines](https://www.notion.so/manychat/Section-Message-1f9b12e9aa1a80c38af4e563fd73af95)
 *
 * [Figma](https://www.figma.com/design/Xf38PdCKeISWnBoh0pLFIIWf/%F0%9F%A7%B1-MC-Desktop-Components?node-id=35164-127&m=dev)
 */
export function SectionMessage({
  variant,
  title,
  description,
  className,
  as: Component = 'div',
  'data-test-id': dataTestId,
}: SectionMessageProps) {
  const iconConfig = iconsConfig[variant]
  const IconComponent = Icon[iconConfig.icon]
  const adoptionAttribute = useDsAdoptionAttribute(AdoptionComponent.SECTION_MESSAGE)

  return (
    <Component
      role="status"
      aria-live="polite"
      className={cx(
        cm.sectionMessage,
        {
          [cm.warning]: variant === 'warning',
          [cm.info]: variant === 'info',
          [cm.error]: variant === 'error',
        },
        className,
      )}
      data-test-id={dataTestId}
      {...adoptionAttribute}
    >
      <div className={cm.container}>
        <div aria-hidden="true" className={cm.icon}>
          <IconComponent size={DEFAULT_ICON_SIZE} color={iconConfig.color} />
        </div>
        <div className={cm.content}>
          {title && <div className={cm.title}>{title}</div>}
          {description && <div>{description}</div>}
        </div>
      </div>
    </Component>
  )
}
